import { useMutation, useQuery } from "@apollo/client";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  GridLegacy as Grid,
  Paper,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { getActivityTitleOnly } from "encamp-shared/src/activity/activityDisplayNames";
import { gql } from "generated-graphql";
import { ActivityType, TierIiReportKind, TierIiReportOrgStatus } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { parseDenialReasonsToText } from "routes/Staff/WorkflowConfig/util.parseDenialReasonsToText";

import { CreateReport } from "../CreateReport";
import { ResetReportButton } from "../ResetReportButton";
import { ResetReportModal } from "../ResetReportModal";
import { useFulfillmentDetails } from "../useFulfillmentDetails";
import { ActivityStepper } from "./ActivityStepper";

export function Activities() {
  const theme = useTheme();
  const { data, isUsingFacility } = useFulfillmentDetails();
  const [resetReportModalOpen, setResetReportModalOpen] = useState(false);

  const currentWorkflow = data?.tierIIReport.currentWorkflow;
  const currentActivityId = data?.tierIIReport.currentWorkflow?.currentActivityId;

  const shouldAutoNavigateToCurrentActivity = useMatch("/staff/fulfillment/r/:reportId/activities");
  const navigate = useNavigate();

  useEffect(() => {
    if (shouldAutoNavigateToCurrentActivity && currentActivityId) {
      navigate(currentActivityId, { replace: true });
    }
  }, [currentActivityId, navigate, shouldAutoNavigateToCurrentActivity]);

  const alerts = useAlerts();
  const ConductReportMutation = gql(`
    mutation ConductReport($reportId: ID!) {
      conductReport(reportId: $reportId) {
        conducted
        notReadyToConduct
        notReadyReason
      }
    }
  `);
  const [conductMutation, { loading: conductMutationLoading }] = useMutation(
    ConductReportMutation,
    {
      variables: { reportId: data?.tierIIReport.id ?? "" },
      refetchQueries: ["GetTierIIReport", "ConductReadiness"],
      onCompleted: (result) => {
        const data = result?.conductReport;
        if (data?.notReadyToConduct) {
          if (data.notReadyReason) {
            alerts.warning(`Report is not ready to conduct: ${data.notReadyReason}`);
          } else {
            alerts.warning("Report is not ready to conduct");
          }
        } else {
          alerts.success("Report is being conducted");
        }
      },
      onError: (error) => {
        const graphQLError = error?.graphQLErrors?.[0];
        const errorCode = graphQLError?.extensions?.code;
        if (errorCode === "REPORT_LOCKED") {
          alerts.warning(graphQLError?.message ?? "Report is locked");
        } else {
          alerts.error(error?.message ?? "Conduct failed");
        }
      },
    }
  );

  const toggleResetModal = useCallback(
    () => setResetReportModalOpen(!resetReportModalOpen),
    [resetReportModalOpen]
  );
  const Fallback = (
    <>
      <Grid
        item
        xs={4}
        sx={{
          display: "flex",
          borderRight: `solid 1px ${theme.palette.divider}`,
        }}
      >
        <WorkflowActionButtons
          sx={{
            mt: "auto",
            width: "100%",
          }}
          reportId={data?.tierIIReport.id}
          facilityId={data?.tierIIReport.facility.id}
          reportKind={data?.tierIIReport.reportKind}
          reportingYear={data?.tierIIReport.reportingYear}
          organizationStatus={data?.tierIIReport.organizationStatus}
          toggleResetModal={toggleResetModal}
          resetReportModalOpen={resetReportModalOpen}
          conductMutation={conductMutation}
          conductMutationLoading={conductMutationLoading}
        />
      </Grid>
      <Grid item xs={8} padding={theme.spacing(2)}>
        <Typography variant="h5">No active workflow</Typography>
        <Typography variant="body1">
          {`The report has not started processing yet. This may be due to the report's verification status or a reset in progress for a previous workflow. The workflow initiation is expected to take around 5 minutes.`}
        </Typography>
      </Grid>
    </>
  );

  const championChips = data?.tierIIReport.currentWorkflow?.activities
    .filter((a: any) =>
      [
        ActivityType.WaitForAllStateReports,
        ActivityType.WaitForAllCnReports,
        ActivityType.WaitForAllAltIdReports,
        ActivityType.WaitForAllCredentialReports,
      ].includes(a.type)
    )
    .map((activity: any, index: number) => {
      const isChampion = activity.championId === data?.tierIIReport.id;
      return (
        <Tooltip key={index} title={getActivityTitleOnly(activity)}>
          <Chip
            icon={isChampion ? <EmojiEvents /> : undefined}
            label={isChampion ? "Champion" : "Non-champion"}
            color="primary"
            sx={{ mt: 1 }}
            onClick={() =>
              navigate(`/staff/fulfillment?omnisearch=cohortChampion:${activity.championId}`)
            }
          />
        </Tooltip>
      );
    });

  return (
    <Grid container component={Paper} elevation={0} minHeight={"32rem"}>
      {isUsingFacility ? (
        <CreateReport />
      ) : !currentWorkflow ? (
        Fallback
      ) : (
        <>
          <Grid
            item
            xs={4}
            sx={{
              borderRight: `solid 1px ${theme.palette.divider}`,
              overflowX: "hidden",
            }}
            display="flex"
            flexDirection="column"
          >
            <Box flex={1} sx={{ overflowY: "auto" }}>
              <Box sx={{ padding: theme.spacing(2, 3, 3, 3) }}>
                <Typography variant="h6" sx={{ color: "gray" }}>
                  Tier II Report
                </Typography>
                {championChips}
              </Box>
              <ActivityStepper />
            </Box>
            <WorkflowActionButtons
              reportId={data?.tierIIReport.id}
              facilityId={data?.tierIIReport.facility.id}
              reportKind={data?.tierIIReport.reportKind}
              reportingYear={data?.tierIIReport.reportingYear}
              organizationStatus={data?.tierIIReport.organizationStatus}
              toggleResetModal={toggleResetModal}
              resetReportModalOpen={resetReportModalOpen}
              conductMutation={conductMutation}
              conductMutationLoading={conductMutationLoading}
            />
          </Grid>
          <Grid display="flex" item xs={8} flexDirection="column">
            <Outlet />
          </Grid>
        </>
      )}
    </Grid>
  );
}

const DENIED_FACILITY_QUERY = gql(`
  query DeniedFacility(
    $search: String
    $sort: [SortModel!]
    $page: Int
    $pageSize: Int
    $reportKind: TierIIReportKind
    $reportingYear: Int
  ) {
    deniedFacilities(
      search: $search
      sort: $sort
      page: $page
      pageSize: $pageSize
      reportKind: $reportKind
      reportingYear: $reportingYear
    ) {
      items {
        facility {
          id
        }
        denialReasons {
          type
          configRule {
            id
            type
            value
         }
        }
      }
    }
  }
`);

const CONDUCT_READINESS_QUERY = gql(`
  query ConductReadiness($reportId: ID!) {
    conductReadiness(reportId: $reportId) {
      isReady
      reason
    }
  }
`);

const WorkflowActionButtons = ({
  reportId,
  facilityId,
  reportKind,
  reportingYear,
  organizationStatus,
  toggleResetModal,
  resetReportModalOpen,
  conductMutation,
  conductMutationLoading,
  sx,
}: {
  reportId?: string;
  facilityId?: string;
  reportKind?: TierIiReportKind | null | undefined;
  reportingYear?: number | null | undefined;
  organizationStatus?: TierIiReportOrgStatus | null | undefined;
  toggleResetModal: () => void;
  resetReportModalOpen: boolean;
  conductMutation: () => void;
  conductMutationLoading: boolean;
  sx?: SxProps;
}) => {
  const theme = useTheme();
  const { data } = useQuery(DENIED_FACILITY_QUERY, {
    variables: {
      search: `facilityId:${facilityId}`,
      reportKind: reportKind,
      reportingYear: reportingYear,
    },
  });

  const { data: readinessData } = useQuery(CONDUCT_READINESS_QUERY, {
    variables: { reportId: reportId ?? "" },
    skip: !reportId,
  });
  const readiness = readinessData?.conductReadiness;
  const isNotReady = readiness?.isReady === false;

  const isDenied = useMemo(() => {
    if (!facilityId || !data?.deniedFacilities.items.length) return false;
    return data.deniedFacilities.items.some((item) => item.facility.id === facilityId);
  }, [data?.deniedFacilities.items, facilityId]);

  const deniedFacility = useMemo(() => {
    if (!isDenied || !facilityId) return null;
    return data?.deniedFacilities.items.find((item) => item.facility.id === facilityId) ?? null;
  }, [data?.deniedFacilities.items, isDenied, facilityId]);

  // A report can only be conducted once it has been verified.
  const isVerified = organizationStatus === TierIiReportOrgStatus.Verified;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={theme.spacing(2)}
      sx={{
        padding: theme.spacing(2, 1, 3, 1),
        justifyContent: "center",
        ...sx,
      }}
    >
      <Box sx={{ minWidth: "140px", flex: "0 0 auto" }}>
        <ResetReportButton onClick={toggleResetModal} />
      </Box>
      <ResetReportModal open={resetReportModalOpen} toggleResetReportModal={toggleResetModal} />
      <Box sx={{ minWidth: "130px", flex: "0 0 auto" }}>
        <Tooltip title={!isVerified ? "Report must be verified before it can be conducted" : ""}>
          {/* span wrapper so the tooltip works while the button is disabled */}
          <span>
            <Button
              variant="contained"
              size="small"
              color="info"
              loadingPosition="start"
              startIcon={<PlayArrowRounded />}
              onClick={() => conductMutation()}
              disabled={!reportId || isDenied || !isVerified || isNotReady}
              loading={conductMutationLoading}
              fullWidth
            >
              Conduct report
            </Button>
          </span>
        </Tooltip>
      </Box>

      {isNotReady && !deniedFacility && (
        <Box sx={{ width: "100%", px: 2 }}>
          <Alert severity="warning" sx={{ fontSize: "0.7rem" }}>
            Report is not ready to conduct
            {readiness?.reason ? `: ${readiness.reason}` : ""}
          </Alert>
        </Box>
      )}

      {facilityId && deniedFacility && (
        <Box sx={{ width: "100%", px: 2 }}>
          <Alert severity="error" sx={{ fontSize: "0.7rem" }}>
            This report cannot be conducted for the following{" "}
            {pluralize("reason", deniedFacility?.denialReasons?.length ?? 0)}:
            {parseDenialReasonsToText(deniedFacility).map((reason) => (
              <Box key={reason}>{reason}</Box>
            ))}
            <Button
              href={`/staff/workflow-config/denied-facilities`}
              sx={{
                mt: theme.spacing(1),
                textDecoration: "none",
                fontSize: "0.7rem",
                textTransform: "none",
              }}
              color="inherit"
            >
              See full list of denied facilities
            </Button>
          </Alert>
        </Box>
      )}
    </Box>
  );
};
